import { useEffect, useEffectEvent, useLayoutEffect, useRef } from 'react';

import { useAtom } from 'jotai';

import { scrollPositionsAtom } from './state';

/**
 * Configuration options for the useScrollMemory hook.
 */
export interface UseStepScrollMemoryProps {
  /**
   * The container element to track scroll position for.
   * Can be a React ref to an HTMLElement, 'body' for document body, or 'window' for window scroll.
   */
  container: React.RefObject<HTMLElement | null> | 'body' | 'window';
  /**
   * Unique identifier for storing and retrieving scroll position.
   * When the key changes, the scroll position for the new key is restored from memory.
   */
  key: string;
  /**
   * Optional flag to control when scroll restoration is active.
   * When false, scroll position is neither saved nor restored.
   * @default true
   */
  enabled?: boolean;
}

/**
 * A React hook that preserves and restores scroll position based on a unique key.
 *
 * This hook automatically:
 * 1. Saves the current scroll position whenever the user scrolls
 * 2. Restores the saved scroll position when the key changes or when enabled becomes true
 *
 * The scroll positions are stored in a Jotai atom, persisting across component re-renders
 * and navigation within the same session.
 *
 * @param {UseStepScrollMemoryProps} [scrollMemory] - Configuration object for scroll memory behavior
 * @param {React.RefObject<HTMLElement | null> | 'body' | 'window'} scrollMemory.container - The scrollable container to track
 * @param {string} scrollMemory.key - Unique identifier for this scroll position
 * @param {boolean} [scrollMemory.enabled=true] - Whether scroll restoration is active
 *
 * @returns {{ clearScrollPosition: () => void }} Object containing utility functions
 * @returns {() => void} clearScrollPosition - Function to clear the scroll position for the current key
 *
 * @example
 * // Basic usage with window/body scrolling
 * import { useScrollMemory } from '@/hooks/useScrollMemory';
 *
 * function SearchResultsPage() {
 *   const { data, isFetching } = useQuery(...);
 *
 *   useScrollMemory({
 *     container: 'body',
 *     key: 'search-results',
 *     enabled: !isFetching, // Only restore after data loads
 *   });
 *
 *   return <div>...</div>;
 * }
 *
 * @example
 * // Using with a scrollable modal
 * ```tsx
 * import { useRef } from 'react';
 * import { useScrollMemory } from '@/hooks/useScrollMemory';
 *
 * function StepModal({ currentStep }: { currentStep: number }) {
 *   const modalBodyRef = useRef<HTMLDivElement>(null);
 *
 *   useScrollMemory({
 *     container: modalBodyRef,
 *     key: `modal-step-${currentStep}`,
 *   });
 *
 *   return (
 *     <div ref={modalBodyRef} style={{ overflow: 'auto' }}>
 *       {/* Step content *\/}
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * // Using with clear functionality
 * import { useScrollMemory } from '@/hooks/useScrollMemory';
 *
 * function DocumentPage() {
 *   const { clearScrollPosition } = useScrollMemory({
 *     container: 'body',
 *     key: 'document-view',
 *   });
 *
 *   const handleReset = () => {
 *     clearScrollPosition(); // Clear saved position
 *     window.scrollTo(0, 0); // Scroll to top
 *   };
 *
 *   return <button onClick={handleReset}>Reset View</button>;
 * }
 *
 * @example
 * // Composite key for nested navigation contexts
 * import { useScrollMemory } from '@/hooks/useScrollMemory';
 *
 * function DocumentDetailsPage({ searchQuery, documentId }: Props) {
 *   const scrollKey = searchQuery
 *     ? `search:${searchQuery}:doc:${documentId}`
 *     : `doc:${documentId}`;
 *
 *   useScrollMemory({
 *     container: 'body',
 *     key: scrollKey,
 *   });
 *
 *   return <div>...</div>;
 * }
 */
export const useScrollMemory = (
  scrollMemory?: UseStepScrollMemoryProps,
): {
  clearScrollPosition: () => void;
} => {
  const containerProp = scrollMemory?.container;
  const key = scrollMemory?.key;
  const enabled = scrollMemory?.enabled ?? true; // Default to true for backward compatibility

  const keyRef = useRef(key ?? '');
  const prevKeyRef = useRef<string | null>(null);
  const prevEnabledRef = useRef<boolean | undefined>(undefined);
  const isRestoringRef = useRef(false);

  // Use Jotai atom for scroll position storage
  const [scrollPositions, setScrollPositions] = useAtom(scrollPositionsAtom);

  // Update key ref when it changes
  useEffect(() => {
    if (key) {
      keyRef.current = key;
    }
  }, [key]);

  const windowScroll = containerProp === 'body' || containerProp === 'window';
  const container = windowScroll
    ? document.documentElement
    : (containerProp?.current ?? null);

  // Save scroll position on scroll
  // Use useEffectEvent to avoid re-creating the effect when key or setScrollPositions changes
  const handleScroll = useEffectEvent(() => {
    // Don't save scroll position if we're currently restoring (to avoid infinite loop)
    if (!enabled || !keyRef.current || isRestoringRef.current || !container)
      return;

    const scrollTop = windowScroll ? window.scrollY : container.scrollTop;

    setScrollPositions((prev: Record<string, number>) => ({
      ...prev,
      [keyRef.current]: scrollTop,
    }));
  });

  useEffect(() => {
    if (!container || !key) return;

    // For window scroll, listen on window; otherwise listen on container
    if (windowScroll) {
      window.addEventListener('scroll', handleScroll, {
        passive: true,
      });
      return () => {
        // Snapshot position on cleanup — React runs all useEffect cleanups
        // before any new setups in the same flush, so this captures the real
        // scroll position before anything (e.g. ScrollToTop) can reset it to 0.
        const snapshotPosition = window.scrollY;
        if (snapshotPosition > 0 && keyRef.current) {
          setScrollPositions(prev => ({
            ...prev,
            [keyRef.current]: snapshotPosition,
          }));
        }
        window.removeEventListener('scroll', handleScroll);
      };
    } else {
      container.addEventListener('scroll', handleScroll, {
        passive: true,
      });
      return () => {
        const snapshotPosition = container.scrollTop;
        if (snapshotPosition > 0 && keyRef.current) {
          setScrollPositions(prev => ({
            ...prev,
            [keyRef.current]: snapshotPosition,
          }));
        }
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [container, windowScroll, key, setScrollPositions]);

  // Restore scroll position when entering a step (only when key changes or enabled becomes true)
  useLayoutEffect(() => {
    if (!container || !key || !enabled) {
      prevEnabledRef.current = enabled;
      return;
    }

    // Only restore when the key actually changes, not when scrollPositions changes
    // This handles navigation between different sections/views.
    const keyChanged =
      prevKeyRef.current === null || prevKeyRef.current !== key;
    prevKeyRef.current = key;

    // Also restore when enabled changes from false to true on the SAME key (e.g., when data finishes loading)
    // This is needed because keyChanged only handles navigation to different sections.
    // When you're on the same page and enabled transitions from false to true, the key hasn't changed,
    // so we need this separate check to restore the scroll position.
    // We check prevEnabledRef.current === false to ensure we only restore on the transition
    // from disabled to enabled, not on every render when enabled is already true.
    const enabledJustBecameTrue = !prevEnabledRef.current;
    prevEnabledRef.current = enabled;

    if (!keyChanged && !enabledJustBecameTrue) return;

    const savedPosition = scrollPositions[key];

    if (!(savedPosition !== undefined && savedPosition > 0)) return;

    // Set flag to prevent saving scroll position during restoration
    isRestoringRef.current = true;

    // Wait for DOM to update, then try to restore
    requestAnimationFrame(() => {
      const maxScroll = windowScroll
        ? document.documentElement.scrollHeight - window.innerHeight
        : container.scrollHeight - container.clientHeight;

      if (maxScroll >= savedPosition) {
        if (windowScroll) {
          window.scrollTo({ top: savedPosition, behavior: 'instant' });
        } else {
          container.scrollTop = savedPosition;
        }
      }

      // Reset flag after a short delay to allow scroll event to settle
      setTimeout(() => {
        isRestoringRef.current = false;
      }, 100);
    });
  }, [container, key, enabled, scrollPositions, windowScroll]);

  // Return function to clear current key's scroll position
  const clearScrollPosition = () => {
    if (key) {
      setScrollPositions((prev: Record<string, number>) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  return { clearScrollPosition };
};
